import React, { useState, useEffect, useMemo } from 'react';
import { fetchDeviceStatus } from '../services/api';
import { RefreshCw, MapPin } from './Icons';
import './DeviceOverview.css';

const HIDDEN_LOCATIONS = new Set([
  'Color Mountain Bike Preserve',
  'Huntly Gravity Zone',
  'Slaughter Pen',
  'Timberline',
  'Wy\'East Academy'
]);

const DeviceOverview = ({ devices, gates, gateAssignments, locations, onSelectDevice }) => {
  const [deviceStatuses, setDeviceStatuses] = useState({});
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [sortField, setSortField] = useState('lastHealthCheck');
  const [sortDesc, setSortDesc] = useState(true);
  const [locationFilter, setLocationFilter] = useState(() => {
    // Restore location filter from localStorage
    try {
      return localStorage.getItem('deviceOverview_locationFilter') || '';
    } catch {
      return '';
    }
  });
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Persist location filter to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('deviceOverview_locationFilter', locationFilter);
    } catch {
      // Silently ignore localStorage errors
    }
  }, [locationFilter]);

  // Create a map of gateId -> gate data
  const gateMap = useMemo(() => {
    const map = {};
    (gates || []).forEach(gate => {
      map[gate.gateId] = gate;
    });
    return map;
  }, [gates]);

  // Create a map of locationId -> location name
  const locationMap = useMemo(() => {
    const map = {};
    (locations || []).forEach(loc => {
      map[loc.id] = loc.name;
    });
    return map;
  }, [locations]);

  // Base device rows derived purely from already-loaded data (devices, gates,
  // assignments, locations). This costs zero Firestore reads — live status is
  // fetched separately and only for the selected location (see below).
  const baseRows = useMemo(() => {
    return devices.map(device => {
      // Get gates assigned to this device
      const assignedGateIds = [];
      Object.entries(gateAssignments || {}).forEach(([gateId, deviceIds]) => {
        if (deviceIds.includes(device.deviceId)) {
          assignedGateIds.push(gateId);
        }
      });

      // Resolve location name: prefer location_id from device, fall back to gate's location_name
      let locationName = null;
      let locationId = device.location_id || null;
      let placement = null;

      if (locationId && locationMap[locationId]) {
        locationName = locationMap[locationId];
      }

      if (assignedGateIds.length > 0) {
        // A device can be assigned to multiple gates, but only some carry
        // location/placement metadata (e.g. per-device test gates have none).
        // Use the first assigned gate that has each value instead of picking a
        // gate by index, which depends on gateAssignments ordering.
        for (const gateId of assignedGateIds) {
          const gate = gateMap[gateId];
          if (!gate) continue;

          if (!locationName && gate.location_name) {
            locationName = gate.location_name;
          }
          if (!placement && gate.placement) {
            placement = gate.placement;
          }

          if (locationName && placement) break;
        }
      }

      return {
        deviceId: device.deviceId,
        locationId,
        locationName,
        placement,
        gateCount: assignedGateIds.length
      };
    });
  }, [devices, gateAssignments, gateMap, locationMap]);

  // Devices belonging to the selected location (no filter selected => none, so
  // navigating to the Devices page issues no status reads).
  const filteredBaseRows = useMemo(() => {
    if (!locationFilter) return [];
    if (locationFilter === 'all') return baseRows;
    return baseRows.filter(row => {
      if (row.locationId) return row.locationId === locationFilter;
      return row.locationName === locationFilter;
    });
  }, [baseRows, locationFilter]);

  // Stable key for the set of devices whose live status we should load.
  // Only fetch live status for specific locations, not for "all" or empty selection.
  const statusFetchKey = useMemo(
    () => (locationFilter && locationFilter !== 'all' ? filteredBaseRows.map(r => r.deviceId).sort().join(',') : ''),
    [locationFilter, filteredBaseRows]
  );

  // Fetch LIVE status only for the selected location's devices. Status is never
  // cached: changing location (or pressing Refresh) always pulls fresh data, so
  // the indicator can't go stale.
  useEffect(() => {
    if (!statusFetchKey) {
      setDeviceStatuses({});
      setLoadingStatuses(false);
      return;
    }

    let cancelled = false;
    const ids = statusFetchKey.split(',');

    const load = async () => {
      setLoadingStatuses(true);
      const statuses = {};
      await Promise.all(
        ids.map(async (deviceId) => {
          try {
            statuses[deviceId] = await fetchDeviceStatus(deviceId);
          } catch (err) {
            console.error(`Error fetching status for ${deviceId}:`, err);
            statuses[deviceId] = null;
          }
        })
      );
      if (!cancelled) {
        setDeviceStatuses(statuses);
        setLoadingStatuses(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [statusFetchKey, refreshNonce]);

  // Merge live status into the visible rows
  const deviceRows = useMemo(() => {
    return filteredBaseRows.map(row => {
      const status = deviceStatuses[row.deviceId];
      const hasStatus = status !== undefined; // null = fetch failed, but still "loaded"
      const lastHealthCheck = status?.health_manager_last_check
        ? new Date(status.health_manager_last_check)
        : null;
      const isOnline = lastHealthCheck && (Date.now() - lastHealthCheck.getTime() < 5 * 60 * 1000);

      return { ...row, lastHealthCheck, isOnline, hasStatus };
    });
  }, [filteredBaseRows, deviceStatuses]);

  // Sort rows
  const sortedRows = useMemo(() => {
    const rows = [...deviceRows];
    rows.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'lastHealthCheck':
          const aTime = a.lastHealthCheck?.getTime() || 0;
          const bTime = b.lastHealthCheck?.getTime() || 0;
          comparison = aTime - bTime;
          break;
        case 'deviceId':
          comparison = a.deviceId.localeCompare(b.deviceId);
          break;
        case 'locationName':
          comparison = (a.locationName || '').localeCompare(b.locationName || '');
          break;
        case 'placement':
          comparison = (a.placement || '').localeCompare(b.placement || '');
          break;
        default:
          comparison = 0;
      }

      return sortDesc ? -comparison : comparison;
    });
    return rows;
  }, [deviceRows, sortField, sortDesc]);

  // Build filter options from the locations prop (all locations in the system)
  const locationFilterOptions = useMemo(() => {
    let locs = [];

    if (locations && locations.length > 0) {
      locs = locations
        .filter(loc => !HIDDEN_LOCATIONS.has(loc.name))
        .map(loc => ({ id: loc.id, name: loc.name }));
    } else {
      // Fallback: derive from base rows if locations prop isn't available
      const locNames = baseRows
        .map(r => r.locationName)
        .filter(Boolean)
        .filter(name => !HIDDEN_LOCATIONS.has(name));
      locs = [...new Set(locNames)].sort().map(name => ({ id: name, name }));
    }

    return locs;
  }, [locations, baseRows]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDesc(!sortDesc);
    } else {
      setSortField(field);
      setSortDesc(true);
    }
  };

  const formatTimestamp = (date) => {
    if (!date) return '—';

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    let relative;
    if (diffMins < 1) {
      relative = 'just now';
    } else if (diffMins < 60) {
      relative = `${diffMins}m ago`;
    } else if (diffHours < 24) {
      relative = `${diffHours}h ago`;
    } else {
      relative = `${diffDays}d ago`;
    }

    return (
      <span title={date.toLocaleString()}>
        {relative}
      </span>
    );
  };

  if (devices.length === 0) {
    return (
      <div className="device-overview">
        <div className="empty-state">
          <p>No devices found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="device-overview">
      <div className="overview-header">
        <h2>Device Status Overview</h2>
        <div className="overview-header-controls">
          {locationFilterOptions.length > 0 && (
            <select
              className="location-filter"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="">Select a location…</option>
              <option value="all">All Devices</option>
              {locationFilterOptions.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          )}
          {locationFilter && (
            <button
              className="refresh-status-btn"
              onClick={() => setRefreshNonce(n => n + 1)}
              disabled={loadingStatuses}
              title="Reload live status"
            >
              <RefreshCw size={14} className={loadingStatuses ? 'spinning' : ''} />
              <span>{loadingStatuses ? 'Loading…' : 'Refresh'}</span>
            </button>
          )}
          <span className="device-count">
            {sortedRows.length}{locationFilter ? ` / ${devices.length}` : ''} device{sortedRows.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {!locationFilter && (
        <div className="overview-location-prompt">
          <MapPin size={16} />
          <span>Select a location to load live device status.</span>
        </div>
      )}

      <div className="overview-table-container">
        <table className="overview-table">
          <thead>
            <tr>
              <th className="col-status">Status</th>
              <th
                className={`col-device sortable ${sortField === 'deviceId' ? 'active' : ''}`}
                onClick={() => handleSort('deviceId')}
              >
                Device
                <span className="sort-indicator">{sortField === 'deviceId' ? (sortDesc ? '▼' : '▲') : ''}</span>
              </th>
              <th
                className={`col-heartbeat sortable ${sortField === 'lastHealthCheck' ? 'active' : ''}`}
                onClick={() => handleSort('lastHealthCheck')}
              >
                Last Heartbeat
                <span className="sort-indicator">{sortField === 'lastHealthCheck' ? (sortDesc ? '▼' : '▲') : ''}</span>
              </th>
              <th
                className={`col-location sortable ${sortField === 'locationName' ? 'active' : ''}`}
                onClick={() => handleSort('locationName')}
              >
                Location
                <span className="sort-indicator">{sortField === 'locationName' ? (sortDesc ? '▼' : '▲') : ''}</span>
              </th>
              <th
                className={`col-placement sortable ${sortField === 'placement' ? 'active' : ''}`}
                onClick={() => handleSort('placement')}
              >
                Placement
                <span className="sort-indicator">{sortField === 'placement' ? (sortDesc ? '▼' : '▲') : ''}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(row => (
              <tr
                key={row.deviceId}
                onClick={() => onSelectDevice(row.deviceId)}
                className="device-row"
              >
                <td className="col-status">
                  {row.hasStatus ? (
                    <span className={`status-indicator ${row.isOnline ? 'online' : 'offline'}`}
                          title={row.isOnline ? 'Online' : 'Offline'}>
                    </span>
                  ) : (
                    <span className="status-indicator unknown"
                          title={loadingStatuses ? 'Loading status…' : 'Select a location to load live status'}>
                    </span>
                  )}
                </td>
                <td className="col-device">
                  <span className="device-name">{row.deviceId}</span>
                  {row.gateCount > 0 && (
                    <span className="gate-badge">{row.gateCount} gate{row.gateCount !== 1 ? 's' : ''}</span>
                  )}
                </td>
                <td className="col-heartbeat">
                  {formatTimestamp(row.lastHealthCheck)}
                </td>
                <td className="col-location">
                  {row.locationName || <span className="not-assigned">Not assigned</span>}
                </td>
                <td className="col-placement">
                  {row.placement || <span className="not-assigned">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="overview-hint">
        Click a row to view device details
      </div>
    </div>
  );
};

export default DeviceOverview;
